import { useId, useState } from 'react';
import { Flex } from 'antd';
import styled from 'styled-components';
import { Heading, Paragraph, Text, Tooltip } from '../Ant';
import { useIsTextTruncated } from '../../hooks/useIsTextTruncated';
import ShowMoreToggle from '../showMoreToggle/showMoreToggle';

export const DataWrapper = styled(Flex)`
  overflow: visible;
  width: 100%;

  .icon {
    font-size: 18px;
    color: var(--clr-title);
    opacity: 0.2;
    flex-shrink: 0;
  }

  .icon[class*='text-clr-'] {
    opacity: 1;
  }
  .conflict-detail-email {
    font-weight: 500;
    color: var(--clr-general-primary);
    text-decoration: underline;
  }
  .content-wrapper {
    width: calc(100% - 23px);
    position: relative;
  }

  .more-action-row {
    width: 100%;
    > :first-child {
      flex: 1;
      min-width: 0;
      word-break: break-all;
    }
    .show-more {
      flex-shrink: 0;
    }
  }
  /* The clamped preview fills both rows up to the toggle; the expanded text wraps between words. */
  .more-action-row.expanded > :first-child {
    word-break: normal;
    overflow-wrap: anywhere;
  }
  .card-data-title {
    word-break: break-word;
  }
`;

interface CardDataProps {
  icon: React.ReactNode;
  title: string;
  content?: React.ReactNode;
  subcontent?: string;
  className?: string;
  type?: string;
  contentclass?: string;
  subcontentclass?: string;
  headingcontent?: React.ReactNode;
  moreAction?: React.ReactNode;
  /** Set where the value is shown in full, so there is nothing for a tooltip to reveal. */
  disableTooltip?: boolean;
  /** Clamps the content to two rows, with a more/less toggle when that cuts it. */
  expandable?: boolean;
}

type ValueSlot = 'content' | 'subcontent';

const CardData = ({
  icon,
  title,
  content,
  subcontent,
  className = '',
  type = '',
  contentclass = '',
  subcontentclass = '',
  headingcontent,
  moreAction,
  disableTooltip = false,
  expandable = false,
}: CardDataProps) => {
  /** A card clamps its subcontent when it has one, so that is where the affordance belongs. */
  const moreActionTarget: ValueSlot = subcontent ? 'subcontent' : 'content';

  const contentId = useId();
  const [isContentExpanded, setIsContentExpanded] = useState(false);
  const [isToggleKeyboardFocused, setIsToggleKeyboardFocused] = useState(false);
  /** Collapsed, the toggle sits beside the two clamped rows; expanded, it follows the last word. */
  const expandLayout = isContentExpanded
    ? { row: 'expanded d-block lh-18', content: 'd-inline' }
    : { row: '', content: 'lineclamp-2' };

  const contentClamp = useIsTextTruncated();
  const subcontentClamp = useIsTextTruncated();
  const clampOf = (slot: ValueSlot) => (slot === 'content' ? contentClamp : subcontentClamp);

  const isMoreActionTarget = (slot: ValueSlot) => Boolean(moreAction) && moreActionTarget === slot;

  const withTooltip = (element: React.ReactElement, text: string | undefined, slot: ValueSlot) => {
    if (disableTooltip || !text) return element;
    /** The more action opens this value in full, so a tooltip would only repeat it. */
    if (isMoreActionTarget(slot)) return element;
    const isClipped = clampOf(slot).isTruncated;
    /** Keyboard users cannot hover, so focusing the toggle reveals the content it expands. */
    const isRevealedByToggle = slot === 'content' && isClipped && isToggleKeyboardFocused;
    return (
      <Tooltip
        title={isClipped ? text : undefined}
        placement="top"
        arrow={false}
        open={isRevealedByToggle || undefined}
      >
        {element}
      </Tooltip>
    );
  };

  const withExpandToggle = (element: React.ReactElement) => {
    if (!expandable) return element;
    const canToggle = isContentExpanded || contentClamp.isTruncated;
    return (
      <Flex align="flex-end" gap={2} className={`more-action-row ${expandLayout.row}`.trim()}>
        {element}
        {/* Flex drops this space while collapsed; expanded, it parts the toggle from the last word. */}{' '}
        {canToggle && (
          <ShowMoreToggle
            expanded={isContentExpanded}
            controls={contentId}
            onToggle={() => setIsContentExpanded((previous) => !previous)}
            onKeyboardFocusChange={setIsToggleKeyboardFocused}
            data-testid={`${type}-card-show-more`}
          />
        )}
      </Flex>
    );
  };

  const withMoreAction = (element: React.ReactElement, slot: ValueSlot) => {
    if (!isMoreActionTarget(slot)) return element;
    return (
      <Flex align="flex-end" gap={2} className="more-action-row">
        {element}
        {clampOf(slot).isTruncated && moreAction}
      </Flex>
    );
  };

  const renderValue = (element: React.ReactElement, text: string | undefined, slot: ValueSlot) =>
    withMoreAction(withTooltip(element, text, slot), slot);

  return (
    <DataWrapper data-testid="card-data" align="flex-start" gap={5} className={className}>
      {icon}
      <div className="ta-left content-wrapper">
        <Flex justify="space-between" align="center">
          <Heading
            level={6}
            $fontsize="11px"
            $weight={400}
            $color="description"
            $lineheight="16px"
            data-testid={`${type}-card-title`}
            className="card-data-title"
          >
            {title}
          </Heading>
          {headingcontent}
        </Flex>
        {withExpandToggle(
          renderValue(
            <Paragraph
              id={expandable ? contentId : undefined}
              ref={contentClamp.ref}
              $fontsize="13px"
              $weight={500}
              $color="title"
              $lineheight="18px"
              data-testid={`${type}-card-content`}
              className={`${contentclass} ${expandable ? expandLayout.content : ''}`.trim()}
            >
              {content}
            </Paragraph>,
            typeof content === 'string' ? content : undefined,
            'content',
          ),
        )}
        {subcontent &&
          renderValue(
            <Text
              ref={subcontentClamp.ref}
              $fontsize="12px"
              $color="subtitle"
              $lineheight="18px"
              data-testid={`${type}-card-subcontent`}
              className={`${subcontentclass}`}
            >
              {subcontent}
            </Text>,
            subcontent,
            'subcontent',
          )}
      </div>
    </DataWrapper>
  );
};

export default CardData;
